import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  Progress,
} from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';

// Lista quem existe no Keycloak (realm kubernet) e em qual grupo, deixa
// trocar o grupo e criar gente nova direto aqui - puxando/gravando pelo
// backend (packages/backend/src/modules/keycloakUsersPlugin.ts). O backend já
// nega (403) quem não está no grupo admin - aqui só exibimos a mensagem.
// Criar usuário morava no Create (Scaffolder, template add-user); passou a
// viver só aqui - ver apps/backstage/README.md, seção "Criando usuários".
const ROLE_GROUPS = ['dev', 'tech-writer', 'devops', 'tech-lead', 'admin'];

type TeamOption = { name: string; displayName: string };

// Mesmo nome hardcoded do backend (keycloakUsersPlugin.ts) - desabilita
// grupo/remoção aqui só pra não deixar a pessoa tentar e levar um 403; a
// garantia de verdade é a checagem do backend.
const PROTECTED_USERNAME = 'admin_backstage';

type KeycloakUserRow = {
  id: string;
  username: string;
  email: string;
  enabled: boolean;
  groups: string[];
  teams: string[];
};

export const UsersPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [users, setUsers] = useState<KeycloakUserRow[] | undefined>();
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [savingId, setSavingId] = useState<string | undefined>();
  const [resetPasswordFor, setResetPasswordFor] = useState<
    { username: string; password: string } | undefined
  >();
  const [deleteTarget, setDeleteTarget] = useState<
    { id: string; username: string } | undefined
  >();

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createUsername, setCreateUsername] = useState('');
  const [createDisplayName, setCreateDisplayName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createGroup, setCreateGroup] = useState('');
  const [createTeamNames, setCreateTeamNames] = useState<string[]>([]);
  const [createdUser, setCreatedUser] = useState<
    { username: string; password: string } | undefined
  >();

  const loadTeams = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('teams');
      const resp = await fetchApi.fetch(`${baseUrl}/teams`);
      if (!resp.ok) {
        return;
      }
      const data = (await resp.json()) as { teams: TeamOption[] };
      setTeams(data.teams);
    } catch {
      // Times são opcionais na criação de usuário - se a lista não carregar,
      // só deixa de oferecer o multi-select, não bloqueia o resto da tela.
    }
  }, [discoveryApi, fetchApi]);

  const loadUsers = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('keycloak-users');
      const resp = await fetchApi.fetch(`${baseUrl}/users`);
      if (resp.status === 403) {
        throw new Error(
          'Só quem está no grupo "admin" do Keycloak pode ver esta lista.',
        );
      }
      if (!resp.ok) {
        throw new Error(`Falha ao carregar usuários (${resp.status})`);
      }
      const data = (await resp.json()) as { users: KeycloakUserRow[] };
      setUsers(data.users);
      setErrorMessage(undefined);
    } catch (e) {
      setErrorMessage((e as Error).message);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    loadUsers();
    loadTeams();
  }, [loadUsers, loadTeams]);

  const handleCreateUser = async () => {
    setCreating(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('keycloak-users');
      const resp = await fetchApi.fetch(`${baseUrl}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: createUsername,
          displayName: createDisplayName,
          email: createEmail,
          group: createGroup,
          teams: createTeamNames,
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao criar usuário (${resp.status})`);
      }
      const data = (await resp.json()) as { password: string };
      setCreatedUser({ username: createUsername, password: data.password });
      setCreateOpen(false);
      setCreateUsername('');
      setCreateDisplayName('');
      setCreateEmail('');
      setCreateGroup('');
      setCreateTeamNames([]);
      await loadUsers();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const handleGroupChange = async (userId: string, group: string) => {
    setSavingId(userId);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('keycloak-users');
      const resp = await fetchApi.fetch(`${baseUrl}/users/${userId}/group`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao trocar grupo (${resp.status})`);
      }
      await loadUsers();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setSavingId(undefined);
    }
  };

  const handleTeamsChange = async (userId: string, teamNames: string[]) => {
    setSavingId(userId);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('keycloak-users');
      const resp = await fetchApi.fetch(`${baseUrl}/users/${userId}/teams`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teams: teamNames }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao atualizar times (${resp.status})`);
      }
      await loadUsers();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setSavingId(undefined);
    }
  };

  const handleResetPassword = async (userId: string, username: string) => {
    setSavingId(userId);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('keycloak-users');
      const resp = await fetchApi.fetch(`${baseUrl}/users/${userId}/reset-password`, {
        method: 'POST',
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao resetar senha (${resp.status})`);
      }
      const data = (await resp.json()) as { password: string };
      setResetPasswordFor({ username, password: data.password });
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setSavingId(undefined);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setSavingId(userId);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('keycloak-users');
      const resp = await fetchApi.fetch(`${baseUrl}/users/${userId}`, {
        method: 'DELETE',
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao remover usuário (${resp.status})`);
      }
      setDeleteTarget(undefined);
      await loadUsers();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setSavingId(undefined);
    }
  };

  const columns: TableColumn<KeycloakUserRow>[] = [
    { title: 'Usuário', field: 'username' },
    { title: 'E-mail', field: 'email' },
    {
      title: 'Grupo / papel',
      field: 'groups',
      render: row => {
        const current = row.groups.find(g => ROLE_GROUPS.includes(g)) ?? '';
        const isProtected = row.username === PROTECTED_USERNAME;
        return (
          <FormControl>
            <Select
              value={current}
              displayEmpty
              disabled={savingId === row.id || isProtected}
              onChange={e => handleGroupChange(row.id, e.target.value as string)}
            >
              <MenuItem value="" disabled>
                sem grupo
              </MenuItem>
              {ROLE_GROUPS.map(g => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      title: 'Times',
      field: 'teams',
      sorting: false,
      render: row => (
        <FormControl style={{ minWidth: 160 }}>
          <Select
            multiple
            displayEmpty
            value={row.teams}
            disabled={savingId === row.id}
            onChange={e => handleTeamsChange(row.id, e.target.value as string[])}
            renderValue={selected =>
              (selected as string[]).length > 0
                ? (selected as string[]).join(', ')
                : 'nenhum'
            }
          >
            {teams.map(t => (
              <MenuItem key={t.name} value={t.name}>
                <Checkbox checked={row.teams.includes(t.name)} />
                <ListItemText primary={t.displayName} secondary={t.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      title: 'Ativo',
      field: 'enabled',
      render: row => (row.enabled ? 'Sim' : 'Não'),
    },
    {
      title: 'Ações',
      field: 'id',
      sorting: false,
      render: row => (
        <>
          <Button
            size="small"
            variant="outlined"
            disabled={savingId === row.id}
            onClick={() => handleResetPassword(row.id, row.username)}
          >
            Resetar senha
          </Button>{' '}
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            disabled={savingId === row.id || row.username === PROTECTED_USERNAME}
            title={
              row.username === PROTECTED_USERNAME
                ? `"${PROTECTED_USERNAME}" é a conta de recuperação do portal e não pode ser removida`
                : undefined
            }
            onClick={() => setDeleteTarget({ id: row.id, username: row.username })}
          >
            Remover
          </Button>
        </>
      ),
    },
  ];

  return (
    <Page themeId="tool">
      <Header
        title="Usuários"
        subtitle="Contas do Keycloak (realm kubernet) e seus grupos"
      />
      <Content>
        {errorMessage && <p>{errorMessage}</p>}
        {!users && !errorMessage && <Progress />}
        <Button variant="contained" color="primary" onClick={() => setCreateOpen(true)}>
          Criar usuário
        </Button>
        {users && (
          <Table
            title={`${users.length} usuário(s)`}
            columns={columns}
            data={users}
            options={{ search: true, paging: users.length > 20 }}
          />
        )}
      </Content>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Criar usuário</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Nome de usuário"
            helperText="Identificador único no Keycloak"
            value={createUsername}
            onChange={e => setCreateUsername(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Nome de exibição"
            value={createDisplayName}
            onChange={e => setCreateDisplayName(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="E-mail"
            type="email"
            value={createEmail}
            onChange={e => setCreateEmail(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="create-user-group-label">Grupo / papel</InputLabel>
            <Select
              labelId="create-user-group-label"
              value={createGroup}
              onChange={e => setCreateGroup(e.target.value as string)}
            >
              {ROLE_GROUPS.map(g => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel id="create-user-teams-label">Times (opcional)</InputLabel>
            <Select
              labelId="create-user-teams-label"
              multiple
              value={createTeamNames}
              onChange={e => setCreateTeamNames(e.target.value as string[])}
              renderValue={selected => (selected as string[]).join(', ')}
            >
              {teams.map(t => (
                <MenuItem key={t.name} value={t.name}>
                  <Checkbox checked={createTeamNames.includes(t.name)} />
                  <ListItemText primary={t.displayName} secondary={t.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button
            color="primary"
            disabled={
              !createUsername || !createDisplayName || !createEmail || !createGroup || creating
            }
            onClick={handleCreateUser}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!createdUser} onClose={() => setCreatedUser(undefined)}>
        <DialogTitle>Usuário criado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Conta criada para <strong>{createdUser?.username}</strong>. Senha
            temporária (a pessoa é obrigada a trocar no primeiro login):
          </DialogContentText>
          <pre>{createdUser?.password}</pre>
          <DialogContentText>
            Essa senha não fica salva em nenhum lugar além desta tela - repasse
            pra pessoa agora, por qualquer canal que vocês já usem.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedUser(undefined)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!resetPasswordFor}
        onClose={() => setResetPasswordFor(undefined)}
      >
        <DialogTitle>Senha resetada</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Nova senha temporária para <strong>{resetPasswordFor?.username}</strong>{' '}
            (a pessoa é obrigada a trocar no primeiro login):
          </DialogContentText>
          <pre>{resetPasswordFor?.password}</pre>
          <DialogContentText>
            Essa senha não fica salva em nenhum lugar além desta tela - repasse
            pra pessoa agora, por qualquer canal que vocês já usem.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordFor(undefined)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(undefined)}>
        <DialogTitle>Remover usuário</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remover <strong>{deleteTarget?.username}</strong> do Keycloak? A conta
            perde acesso ao portal imediatamente e isso não pode ser desfeito.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(undefined)}>Cancelar</Button>
          <Button
            color="secondary"
            disabled={!!deleteTarget && savingId === deleteTarget.id}
            onClick={() => deleteTarget && handleDeleteUser(deleteTarget.id)}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};
